import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import { mixed, object, string } from "yup";
import ApplicationVerifyDialog from "./ApplicationVerifyDialog";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

type Props = {
  onClose: () => void;
  onSubmit: (formData: any) => any;
  type:string;
  category:string;
  dobProofType?:string;
  paymentCategory?:any;
  /**
   * PAN application type - NEW or CORRECTION. A correction is filed against a
   * PAN that already exists, so its form carries no Part D and there is no AO
   * Code to ask for; only a NEW application needs one. It is also what decides
   * whether the applicant's residence address is asked for - see
   * `showResidenceAddress` in ApplicationVerifyDialog.
   */
  applicationType?:string;
  /**
   * Heading override. PAN calls this step "login" rather than "verify".
   */
  title?:string;
  /**
   * Status to post. Defaults to VERIFY, which is still where this dialog lands
   * for ITR / MSME / Gumasta / DSC. PAN captures the same acknowledgement details
   * but at the front of its flow, so it posts FORM_FILL_PENDING instead.
   */
  requestedStatus?:string;
};

export type VerificationInitialValues = {
  acknowledgement: any;
  remark: string;
  /** AO Code — PAN only, captured in place of the remark. */
  aoAreaCode: string;
  aoType: string;
  aoRangeCode: string;
  aoNo: string;
  fileType: string;
  acknowledgement_address:string;
  acknowledgement_name:string;
  acknowledgement_father_name:string;
  acknowledgement_place:string;
  acknowledgementNumber: string;
};

const ApplicationVerifyDialogWrapper = ({ onClose, onSubmit ,type, category, dobProofType, paymentCategory, applicationType, title, requestedStatus = "VERIFY" }: Props) => {
  /*
    The AO Code is asked for on a NEW PAN application and nowhere else - not on
    a PAN correction, whose form has no Part D cells to pre-fill, and not on any
    other service, where an AO code means nothing.
  */
  const needsAOCode = type === "PAN" && applicationType !== "CORRECTION";
  /*
    Form Validation Schema.

    The AO Code is mandatory on a NEW PAN application and only there. It is
    captured here, at login, because this is the moment it is looked up — and
    the PAN form's Part D cells are pre-filled from it later with no second
    chance to ask. An application verified without it reaches the form-fill
    operator with four empty boxes and nothing to fill them from.

    A PAN correction has no Part D, so it is not asked for and not required.
    Every other service still takes a free-text remark, which stays optional.
  */
  const validationSchema = object({
    acknowledgement: mixed().required("Acknowledgement is required"),
    ...(needsAOCode
      ? {
          aoAreaCode: string().trim().required("Area Code is required"),
          aoType: string().trim().required("AO Type is required"),
          aoRangeCode: string().trim().required("Range Code is required"),
          aoNo: string().trim().required("AO No. is required"),
        }
      : {}),
  });

  // Form Initial Values
  const initialValues: VerificationInitialValues = {
    acknowledgement: "",
    remark: "",
    aoAreaCode: "",
    aoType: "",
    aoRangeCode: "",
    aoNo: "",
    acknowledgement_address: "",
    acknowledgement_name: "",
    acknowledgement_father_name: "",
    acknowledgement_place: "",
    fileType: "ACKNOWLEDGEPDF",
    acknowledgementNumber: "",
  };

  // Form Submit Handler
  const handleSubmit = (
    values: VerificationInitialValues,
    { setSubmitting }: FormikHelpers<VerificationInitialValues>
  ) => {
    const formData = new FormData();

    formData.append("requestedStatus", requestedStatus);
    formData.append("fileType", values?.fileType);
    /*
      A NEW PAN posts the AO Code the dialog captured; everything else — a PAN
      correction included — posts the remark it captured. Posting the key the
      dialog never showed would write an empty string over whatever is already
      on the application.
    */
    if (needsAOCode) {
      formData.append("aoAreaCode", values?.aoAreaCode);
      formData.append("aoType", values?.aoType);
      formData.append("aoRangeCode", values?.aoRangeCode);
      formData.append("aoNo", values?.aoNo);
    } else {
      formData.append("remark", values?.remark);
    }
    formData.append("acknowledgement_address", values?.acknowledgement_address);
    formData.append("acknowledgement_place", values?.acknowledgement_place);
    formData.append("acknowledgementNumber", values?.acknowledgementNumber);
    formData.append("file", values?.acknowledgement);
    onSubmit(formData).then((res: any) => {
      if (res?.error) {
        setSubmitting(false);
      } else {
        setSubmitting(false);
        onClose();
      }
    });
  };

  return (
    <Dialog open={true} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div className="flex justify-start text-primary-main font-bold">
          {title || "Verfiy Application"}
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {(formikProps) => (
            <Form className="h-full">
              <ApplicationVerifyDialog
                formikProps={formikProps}
                onClose={onClose}
                type={type}
                category={category}
                dobProofType={dobProofType}
                paymentCategory={paymentCategory}
                applicationType={applicationType}
                showAOCode={needsAOCode}
              />
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationVerifyDialogWrapper;
